import $ from 'jquery';

export const mediaBreakpoints = {
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1400,
};

/**
 * Adjusts the position and width of a dropdown menu based on the viewport size and content.
 *
 * @param {HTMLElement} dropdownItem - The dropdown item containing the menu link and dropdown menu.
 */
export const adjustDropdownPosition = (dropdownItem) => {
  const DROPDOWN_MIN_WIDTH = 632; // Minimum width for the dropdown menu
  const DROPDOWN_EXTENDED_MIN_WIDTH = 868;
  const XXL_BREAKPOINT_OFFSET = 50;

  // Find the menu link and dropdown menu within the dropdown item
  const menuLink = dropdownItem.querySelector('.menu-link');
  const dropdownMenu = dropdownItem.querySelector('.dropdown-menu');
  const colElements = dropdownMenu?.querySelectorAll('.col') || [];

  // Return early if essential elements are missing
  if (!menuLink || !dropdownMenu) return;

  // Get the current viewport width
  const pageWidth = window.innerWidth;

  // Get the bounding rectangle of the menu link
  const menuLinkRect = menuLink.getBoundingClientRect();

  // Determine dropdown width
  const dropdownWidth =
    colElements.length > 6 ? DROPDOWN_EXTENDED_MIN_WIDTH : DROPDOWN_MIN_WIDTH;

  // Reset styles
  dropdownMenu.style.right = '';
  dropdownMenu.style.left = '';
  dropdownMenu.style.width = `${dropdownWidth}px`;

  const spaceToRightEdge = pageWidth - menuLinkRect.left;

  //if (pageWidth < mediaBreakpoints.xxl + XXL_BREAKPOINT_OFFSET) {
  if (dropdownWidth > spaceToRightEdge) {
    // Not enough space on the right, shift to the left
    const shiftLeft = dropdownWidth - spaceToRightEdge;
    dropdownMenu.style.left = `-${shiftLeft}px`;
  } else {
    // Enough space, align normally
    dropdownMenu.style.left = `0px`;
  }
  //}
};

/**
 * Debounce a function, delaying its execution until after a specified wait time has elapsed
 * since the last time it was invoked.
 *
 * @param {Function} func - The function to debounce.
 * @param {number} wait - The time to wait in milliseconds.
 * @returns {Function} - A debounced version of the provided function.
 */
export const debounce = (func, wait = 100) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
};
/**
 * Adjusts the dropdown toggle button elements.
 */
export const updateDropdownToggle = () => {
  const dropdownToggleItems = document.querySelectorAll('.dropdown-toggle');

  if (dropdownToggleItems) {
    dropdownToggleItems.forEach(adjustDropdownToggle);
  }
};

export const initMobileNavDropdowns = () => {
  const LG = mediaBreakpoints.lg;
  document.querySelectorAll('#top-navbar .nav-item.dropdown').forEach((dropdown) => {
    const toggle = dropdown.querySelector('.dropdown-toggle');
    const menu = dropdown.querySelector('.dropdown-menu');
    if (!toggle || !menu) return;

    toggle.addEventListener('click', () => {
      if (window.innerWidth >= LG) return;
      if (menu.classList.contains('show')) {
        menu.classList.remove('show');
        dropdown.classList.remove('show');
        toggle.classList.remove('show');
        toggle.setAttribute('aria-expanded', 'false');
      } else {
        menu.classList.add('show');
        dropdown.classList.add('show');
        toggle.classList.add('show');
        toggle.setAttribute('aria-expanded', 'true');
      }
    });
  });
};

/**
 * Updates the `data-bs-toggle` attribute on a dropdown toggle element
 * based on the current window width. Ensures dropdown behavior only applies on smaller screens.
 *
 * @function adjustDropdownToggle
 * @param {HTMLElement} dropdownToggleItem - The toggle element to update.
 * @returns {void}
 */
export const adjustDropdownToggle = (dropdownToggleItem) => {
  dropdownToggleItem.removeAttribute('data-bs-toggle');
};

/**
 * Updates the position of all dropdown menu items on the page.
 *
 * This function:
 * - Selects all elements with the `.nav-item.dropdown` class.
 * - Calls `adjustDropdownPosition` for each, aligning dropdowns based on viewport and content.
 *
 * @function updateDropdownPositions
 * @returns {void}
 */
export const updateDropdownPositions = () => {
  const dropdownItems = document.querySelectorAll('.nav-item.dropdown');

  if (dropdownItems) {
    dropdownItems.forEach(adjustDropdownPosition);
  }
};

/**
 * Adds smooth scrolling behavior to elements with the `.js-anchor-link` class.
 * When clicked, it prevents the default action and scrolls to the element with the corresponding `id`.
 * Adds an offset of XXXpx from the top of the viewport.
 */
export const anchorLinks = (offset = 100) => {
  const anchorActivators = document.querySelectorAll('.js-anchor-link');

  if (anchorActivators) {
    anchorActivators.forEach((item) => {
      item.addEventListener('click', (event) => {
        event.preventDefault();

        const anchorTarget = event.target?.dataset?.anchor;

        if (!anchorTarget) return;

        const targetElement = document.getElementById(anchorTarget);

        if (targetElement) {
          const elementPosition =
            targetElement.getBoundingClientRect().top + window.scrollY;

          window.scrollTo({
            top: elementPosition - offset,
            behavior: 'smooth',
          });
        }
      });
    });
  }
};

/**
 * Validates form fields before submission.
 * @param {jQuery} form - jQuery object of the form
 * @param {object} fieldsSet - object with fieldsSet
 * @param {boolean} showError - show/hide error message
 * @returns {boolean} - Returns true if all fields are valid, otherwise false
 */
const validateFormFields = (form, fieldsSet, showError = true) => {
  let isValid = true;
  const formDomElement = form[0];

  // Clearing previous error messages
  form.find('.not-valid').removeClass('not-valid');
  form.find('.validation-error').remove();

  // Configuring validation rules and messages
  const validationRules = {
    firstName: {
      required: true,
      pattern: /^[A-Za-z\s]+$/,
      minLength: 2,
      maxLength: 50,
      messages: {
        required: 'First Name is required.',
        pattern: 'First Name can only contain letters.',
        minLength: 'First Name must be at least 2 characters long.',
        maxLength: 'First Name must be at most 50 characters long.',
      },
    },
    lastName: {
      required: true,
      pattern: /^[A-Za-z\s]+$/,
      minLength: 2,
      maxLength: 50,
      messages: {
        required: 'Last Name is required.',
        pattern: 'Last Name can only contain letters.',
        minLength: 'Last Name must be at least 2 characters long.',
        maxLength: 'Last Name must be at most 50 characters long.',
      },
    },
    referralSource: {
      required: true,
      pattern: /^[A-Za-z_\-\s]+$/,
      messages: {
        required: 'Field is required.',
      },
    },
    questionType: {
      required: true,
      pattern: /^[A-Za-z_\-\s]+$/,
      messages: {
        required: 'Field is required.',
      },
    },
    phone: {
      required: false,
      pattern: /^\+\d{10,15}$/,
      maxLength: 15,
      messages: {
        pattern:
          "The phone number must start with '+' and contain 10-15 digits.",
        maxLength:
          'If provided, Phone number must be at most 15 characters long.',
      },
    },
    email: {
      required: true,
      pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      messages: {
        required: 'Email is required.',
        pattern: 'Email must be a valid email address.',
      },
    },
    message: {
      required: false,
      minLength: 10,
      maxLength: 500,
      messages: {
        minLength: 'Message must be at least 10 characters long.',
        maxLength: 'Message must be at most 500 characters long.',
      },
    },
    cvFromUrl: {
      required: false,
      pattern: /^(https?:\/\/)[^\s/$.?#].[^\s]*$/i,
      messages: {
        pattern:
          'Please upload your CV, or enter it manually, or provide a valid URL.',
      },
    },
  };

  for (const [key, selector] of Object.entries(fieldsSet)) {
    const $field = form.find(selector);
    const rule = validationRules[key];
    const value = $field.val()?.trim() || '';

    if (!$field.length || !rule) continue;

    // Required check
    if (rule.required && !value) {
      if (showError) showValidationError($field, rule.messages.required);
      isValid = false;
      continue;
    }

    // Pattern (if there is and the field is not empty)
    if (rule.pattern && value && !rule.pattern.test(value)) {
      if (showError) showValidationError($field, rule.messages.pattern);
      isValid = false;
    }

    // Min length
    if (rule.minLength && value.length < rule.minLength) {
      if (showError) showValidationError($field, rule.messages.minLength);
      isValid = false;
    }

    // Max length
    if (rule.maxLength && value.length > rule.maxLength) {
      if (showError) showValidationError($field, rule.messages.maxLength);
      isValid = false;
    }
  }

  // additional check for CV fields
  if (formDomElement?.name === 'apply_cv') {
    const selectedFiles = formDomElement.querySelector(
      'input[name="apply_cv[resume_cv_attachment]"]',
    );
    const cvFromUrl = formDomElement.querySelector(
      'input[name="apply_cv[cv_from_url]"]',
    );
    const cvManual = formDomElement.querySelector(
      'textarea[name="apply_cv[cv_manual]"]',
    );

    const fileVal = selectedFiles?.value.trim();
    const urlVal = cvFromUrl?.value.trim();
    const manualVal = cvManual?.value.trim();

    const hasFile = !!fileVal;
    const hasValidUrl =
      !!urlVal && validationRules.cvFromUrl.pattern.test(urlVal);
    const hasManualText = !!manualVal;

    // If none of the fields are filled in or invalid - error
    if (!hasFile && !hasValidUrl && !hasManualText) {
      isValid = false;

      if (showError) {
        const errorMsg =
          'Please upload your CV, enter it manually, or provide a valid URL.';
        const cvSection = formDomElement.querySelector(
          '.js-cv-section-required',
        );
        cvSection && showValidationError($(cvSection), errorMsg);
      }
    }
  }

  return isValid;
};

/**
 * Displays a validation error message below the invalid input field.
 * @param {jQuery} field - The jQuery object of the input field
 * @param {string} message - The validation error message
 */
const showValidationError = (field, message) => {
  const tagName = field?.[0]?.tagName?.toLowerCase();
  field.addClass('not-valid');

  if (tagName === 'select') {
    const parent = field?.[0].closest('.choices');
    parent &&
      $(parent).after(
        `<span class="validation-error">${message}</span>`,
      );
  } else {
    field.after(
      `<span class="validation-error">${message}</span>`,
    );
  }
};

/**
 * Handles the state of a form submit button based on a user agreement checkbox.
 *
 * - Disables the submit button when the checkbox is unchecked.
 * - Enables the submit button when the checkbox is checked.
 *
 * @function userAgreeCheckbox
 * @returns {void}
 */
export const userAgreeCheckbox = (fieldsSet) => {
  const checkbox = document.querySelector('.js-user-agree-checkbox');
  if (!checkbox) return;

  const parentForm = checkbox.closest('form');
  if (!parentForm) return;

  const btnEl = parentForm.querySelector('.js-form-submit-button');
  if (!btnEl) return;

  checkbox.addEventListener('change', (event) => {
    // const isFieldsValid = validateFormFields($(parentForm), fieldsSet, false);
    const operationType = event.target?.checked ? 'remove' : 'add';

    btnEl.classList[operationType]('disabled');
  });
};

/**
 * Handles the submission of the contact form using AJAX.
 * Prevents default form submission and sends form data via an asynchronous request.
 * Updates the form container with the response or logs an error in case of failure.
 */
export const formSubmitHandler = (formName, successMessageWrapper) => {
  if (!formName || !successMessageWrapper) return;

  const form = $(`form[name="${formName}"]`);
  if (!form.length) return;

  let isFieldValid = false;

  const submitBtn = document.querySelector(
    `form[name="${formName}"] button[type="submit"]`,
  );

  const fieldsSet = {
    apply_cv: {
      firstName: '[name="apply_cv[firstName]"]',
      lastName: '[name="apply_cv[lastName]"]',
      email: '[name="apply_cv[email]"]',
      phone: '[name="apply_cv[phone]"]',
      cvFromUrl: '[name="apply_cv[cv_from_url]"]',
    },
    contact: {
      firstName: '[name="contact[firstName]"]',
      lastName: '[name="contact[lastName]"]',
      email: '[name="contact[email]"]',
      phone: '[name="contact[phone]"]',
      message: '[name="contact[message]"]',
    },
    contact_us: {
      firstName: '[name="contact_us[firstName]"]',
      lastName: '[name="contact_us[lastName]"]',
      email: '[name="contact_us[email]"]',
      phone: '[name="contact_us[phone]"]',
      message: '[name="contact_us[message]"]',
      referralSource: '[name="contact_us[referralSource]"]',
      questionType: '[name="contact_us[questionType]"]',
    },
    answer_form: {
      firstName: '[name="answer_form[firstName]"]',
      lastName: '[name="answer_form[lastName]"]',
      email: '[name="answer_form[email]"]',
      phone: '[name="answer_form[phone]"]',
      message: '[name="answer_form[message]"]',
    },
  };

  submitBtn &&
    submitBtn.addEventListener('click', (event) => {
      isFieldValid = validateFormFields(form, fieldsSet[formName]);
      const operationType = isFieldValid ? 'remove' : 'add';
      form[0].classList?.[operationType]('is-invalid');
    });

  form.on('submit', function (e) {
    e.preventDefault();

    if (e.target.classList.contains('is-invalid')) {
      return false;
    }

    const jobTitleInput = document.querySelector('#apply_cv_vacancy_title');
    const vacancyTitle = document.querySelector('.js-vacancy-title');

    if (jobTitleInput && vacancyTitle) {
      jobTitleInput.value = vacancyTitle.innerText;
    }

    const formData = new FormData(this);

    $.ajax({
      url: form.attr('action'),
      method: form.attr('method'),
      data: formData,
      processData: false,
      contentType: false,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
      success: function (content) {
        $(successMessageWrapper).html(content);
      },
      error: function (jqXHR, textStatus, errorThrown) {
        console.error('Error:', errorThrown);
      },
    });
  });

  /*
	const requiredFieldsSelector = Object.values(fieldsSet[formName]).join(', ');
	const requiredFields = form.find(requiredFieldsSelector);

	const parentFormEl = form[0];
	const checkbox = parentFormEl && parentFormEl.querySelector('.js-user-agree-checkbox');

	// Checking fields validation on the fly to enable/disable "Submit" button
	if (requiredFields.length && checkbox && submitBtn) {
		// Attach debounced input handler for validation
		const debouncedInputHandler = debounce((event) => {

			console.log('event', event);

			const isFieldsValid = validateFormFields($(parentFormEl), fieldsSet[formName], false);
			const operationType = (checkbox.checked && isFieldsValid) ? 'remove' : 'add';
			submitBtn.classList[operationType]('disabled');
		}, 300);

		requiredFields.each(function () {
			const tagName = this.tagName.toLowerCase();
			const type = this.type?.toLowerCase();

			if (tagName === 'textarea' || type === 'text' || type === 'email') {
				this.addEventListener('input', debouncedInputHandler);
			} else {
				this.addEventListener('change', debouncedInputHandler);
			}
		});
	}*/

  userAgreeCheckbox(fieldsSet[formName]);
};

/**
 * Handles filtering of vacancy items based on form input.
 *
 * This function:
 * - Listens for the submission of a vacancies filter form.
 * - Filters vacancy items based on selected team, location, and search query.
 * - If a filter value is empty, it is ignored in the filtering process.
 * - Adds a `hidden` class to elements that do not match the criteria.
 * - Ensures that parent vacancy lists are hidden if they contain no visible items.
 */
export const filteringVacancies = () => {
  const vacanciesFilterForm = document.querySelector(
    '.js-vacancies-filter-form',
  );
  const locationFilterInForm = document.querySelector('.js-select-location');
  const teamFilterInForm = document.querySelector('.js-select-team');
  const vacanciesItemsWrapper = document.querySelector('.js-vacancies-items');
  const noDataWrapper = document.querySelector('.js-vacancies-items-no-data');

  // If the form is not found, exit the function
  if (!vacanciesFilterForm) return false;

  vacanciesFilterForm.addEventListener('submit', (event) => {
    event.preventDefault();

    // Get filter values
    const team = event.target?.team?.value?.trim();
    const location = event.target?.location?.value?.trim();
    const searchQuery = event.target?.['search-vacancy-query']?.value
      ?.trim()
      .toLowerCase();

    const vacancyItems = document.querySelectorAll('.js-vacancy-item');
    const vacancyItemsLists = document.querySelectorAll(
      '.js-vacancy-items-list',
    );

    let visibleVacanciesCount = 0; // Counter for visible vacancies

    vacancyItems.forEach((item) => {
      const itemTeam = item.dataset.team?.trim().toLowerCase();
      const itemLocation = item.dataset.location?.trim().toLowerCase();
      const itemTitle = item.dataset.title?.trim().toLowerCase();

      // Filtering logic: if the filter value is empty, ignore it
      const matchTeam = team === '' || itemTeam === team.toLowerCase();
      const matchLocation =
        location === '' || itemLocation === location.toLowerCase();
      const matchSearch = searchQuery === '' || itemTitle.includes(searchQuery);

      // Show or hide the element based on filtering
      if (matchTeam && matchLocation && matchSearch) {
        item.classList.remove('hidden');
        visibleVacanciesCount++; // Increase the counter if the vacancy is visible
      } else {
        item.classList.add('hidden');
      }
    });

    let hasVisibleVacancies = false; // Flag to check if there are visible vacancies

    // Check if there are any visible vacancies in the parent container
    vacancyItemsLists.forEach((list) => {
      const visibleItems = list.querySelectorAll(
        '.js-vacancy-item:not(.hidden)',
      );

      if (visibleItems.length > 0) {
        list.classList.remove('hidden');
        hasVisibleVacancies = true;
      } else {
        list.classList.add('hidden');
      }
    });

    // Show or hide the "No vacancies found" block
    if (visibleVacanciesCount > 0) {
      noDataWrapper?.classList.add('hidden');
    } else {
      noDataWrapper?.classList.remove('hidden');
    }
  });

  // Automatically submit the form when a filter option changes
  locationFilterInForm?.addEventListener('change', () => {
    vacanciesFilterForm.requestSubmit();
  });

  teamFilterInForm?.addEventListener('change', () => {
    vacanciesFilterForm.requestSubmit();
  });
};

/**
 * Uploads a single file to the server via a POST request.
 *
 * @async
 * @function uploadFileToServer
 * @param {File} file - The file object to be uploaded.
 * @returns {Promise<Object|null>} - Returns the server response as a JSON object if successful, or `null` on failure.
 */
const uploadFileToServer = async (file) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch('/upload/cv', {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    alert('File uploads error');
    return null;
  }

  return await response.json();
};

/**
 * Handles the file upload popup for the CV form.
 *
 * This function manages:
 * - Opening and closing the popup
 * - Selecting and displaying uploaded files
 * - Removing files from the list
 * - Updating the main form with selected files
 */
export const cvFormPopupHandler = () => {
  const openUploadPopupBtns = document.querySelectorAll(
    '.js-open-uploader-popup',
  );
  const closePopupBtns = document.querySelectorAll(
    '.js-close-icon, .js-close-btn',
  );

  const fileInputs = document.querySelectorAll('.js-uploader-input');
  const submitBtns = document.querySelectorAll('.js-submit-btn');

  const formFileInputCV = document.getElementById(
    'apply_cv_resume_cv_attachment',
  );
  const formFileInputCL = document.getElementById('apply_cv_cl_attachment');

  // Main storage for uploaded files
  let uploadedFiles = {
    cv: [],
    cl: [],
  };

  // Temporary storage for newly added files within the current popup session
  let tempUploadedFiles = {
    cv: [],
    cl: [],
  };

  openUploadPopupBtns.forEach((item) => {
    item?.addEventListener('click', () => {
      const popupType = item.dataset.type;
      const uploadPopup = document.querySelector(
        `.js-popup[data-type="${popupType}"]`,
      );

      // Reset temp storage when opening a popup
      tempUploadedFiles[popupType] = [];

      uploadPopup.classList.add('active');
    });
  });

  const cleaningPopupTemporaries = (popupType) => {
    const fileListContainer = document.querySelector(
      `.js-popup[data-type="${popupType}"] .js-selected-files-overview`,
    );
    const selectedFilesWrapper = document.querySelector(
      `.js-popup[data-type="${popupType}"] .js-selected-files-wrapper`,
    );
    const fileCounter = document.querySelector(
      `.js-popup[data-type="${popupType}"] .js-popup-files-counter`,
    );

    if (fileListContainer) {
      fileListContainer.classList.remove('selected');
      fileListContainer.innerHTML = '';
    }

    if (fileCounter) {
      fileCounter.textContent = '0';
    }
    selectedFilesWrapper && selectedFilesWrapper.classList.remove('selected');
    tempUploadedFiles[popupType] = [];
  };

  closePopupBtns.forEach((btn) => {
    btn.addEventListener('click', (event) => {
      const popupType = event.target?.dataset.type;
      const uploadPopup = document.querySelector(
        `.js-popup[data-type="${popupType}"]`,
      );

      // Remove temporarily added files if the user closes the popup without submitting
      updateFileList(popupType, 'remove');
      updateFileCounter(popupType, 'zero');
      cleaningPopupTemporaries(popupType);
      toggleSubmitButton(popupType);

      uploadPopup && uploadPopup.classList.remove('active');
    });
  });

  const renderSelectedFiles = (
    popupType,
    arrayWithData,
    destinationWrapper,
  ) => {
    if (!destinationWrapper || !arrayWithData || !popupType) return;

    if (destinationWrapper) {
      destinationWrapper.innerHTML = '';
    }

    arrayWithData.forEach((file) => {
      const fileSize = (file.size / 1024 / 1024).toFixed(2); // MB
      const fileItem = document.createElement('div');
      fileItem.classList.add(
        'popup__selected-file-item',
        'js-selected-file-item',
      );

      fileItem.innerHTML = `
                <svg width="20" height="20" fill="none">
                    <rect width="20" height="20" rx="4" fill="#F6F7F8"></rect>
                    <path d="M8 14h4c.19 0 .35-.06.48-.17.13-.12.2-.28.2-.5 0-.21-.07-.38-.2-.5a.69.69 0 00-.48-.17H8a.69.69 0 00-.48.17c-.13.12-.2.28-.2.5 0 .21.07.38.2.5.13.11.29.17.48.17zm0-2.67h4c.19 0 .35-.06.48-.17.13-.12.2-.28.2-.5 0-.21-.07-.38-.2-.5a.69.69 0 00-.48-.17H8a.69.69 0 00-.48.17c-.13.12-.2.28-.2.5 0 .21.07.38.2.5.13.11.29.17.48.17zm-2 3.33c-.37 0-.69-.13-.94-.38-.25-.26-.38-.58-.38-.95V4.67c0-.37.13-.69.38-.94.25-.25.57-.38.94-.38h4.78c.18 0 .35.03.5.1.15.06.29.14.41.26l3.23 3.23c.12.12.21.26.26.41.06.15.09.32.09.5v7.44c0 .37-.13.69-.38.95-.25.26-.57.38-.94.38H6zm7.65-11.11L10.78 7.33H6v10.56h8V7.96H11.33c-.19 0-.35-.06-.48-.17a.69.69 0 01-.17-.48z" fill="#9CA3AF"></path>
                </svg>
                <div class="popup__selected-file-info">
                    <div class="popup__selected-file-name js-selected-file-name">${file.name}</div>
                    <div class="popup__selected-file-size js-selected-file-size">${fileSize} MB</div>
                </div>
                <svg data-type="${popupType}" class="popup__selected-file-remove-btn js-selected-file-remove-btn" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M6.35425 16.6667C5.95321 16.6667 5.60989 16.5216 5.3243 16.2315C5.03871 15.9414 4.89591 15.5926 4.89591 15.1852V5.5556C4.68932 5.5556 4.51614 5.48461 4.37638 5.34263C4.23663 5.20066 4.16675 5.02473 4.16675 4.81486C4.16675 4.60498 4.23663 4.42905 4.37638 4.28708C4.51614 4.1451 4.68932 4.07411 4.89591 4.07411H7.81258C7.81258 3.86424 7.88246 3.68831 8.02222 3.54634C8.16197 3.40436 8.33515 3.33337 8.54175 3.33337H11.4584C11.665 3.33337 11.8382 3.40436 11.9779 3.54634C12.1177 3.68831 12.1876 3.86424 12.1876 4.07411H15.1042C15.3108 4.07411 15.484 4.1451 15.6238 4.28708C15.7635 4.42905 15.8334 4.60498 15.8334 4.81486C15.8334 5.02473 15.7635 5.20066 15.6238 5.34263C15.484 5.48461 15.3108 5.5556 15.1042 5.5556V15.1852C15.1042 15.5926 14.9615 15.9414 14.6759 16.2315C14.3903 16.5216 14.047 16.6667 13.6459 16.6667H6.35425ZM13.6459 5.5556H6.35425V15.1852H13.6459V5.5556ZM8.54175 13.7037C8.74834 13.7037 8.92152 13.6328 9.06128 13.4908C9.20104 13.3488 9.27091 13.1729 9.27091 12.963V7.77782C9.27091 7.56794 9.20104 7.39202 9.06128 7.25004C8.92152 7.10807 8.74834 7.03708 8.54175 7.03708C8.33515 7.03708 8.16197 7.10807 8.02222 7.25004C7.88246 7.39202 7.81258 7.56794 7.81258 7.77782V12.963C7.81258 13.1729 7.88246 13.3488 8.02222 13.4908C8.16197 13.6328 8.33515 13.7037 8.54175 13.7037ZM11.4584 13.7037C11.665 13.7037 11.8382 13.6328 11.9779 13.4908C12.1177 13.3488 12.1876 13.1729 12.1876 12.963V7.77782C12.1876 7.56794 12.1177 7.39202 11.9779 7.25004C11.8382 7.10807 11.665 7.03708 11.4584 7.03708C11.2518 7.03708 11.0786 7.10807 10.9389 7.25004C10.7991 7.39202 10.7292 7.56794 10.7292 7.77782V12.963C10.7292 13.1729 10.7991 13.3488 10.9389 13.4908C11.0786 13.6328 11.2518 13.7037 11.4584 13.7037Z" fill="#9CA3AF"/>
                </svg>
            `;

      destinationWrapper.appendChild(fileItem);
    });
  };

  /**
   * Updates the list of downloaded files in the popup
   */
  function updateFileList(popupType, operation = 'add') {
    const fileListContainer = document.querySelector(
      `.js-popup[data-type="${popupType}"] .js-selected-files-overview`,
    );

    updateFileCounter(popupType, operation);

    const newFiles =
      operation === 'add'
        ? [...tempUploadedFiles[popupType]]
        : uploadedFiles[popupType].filter(
            (file) =>
              !tempUploadedFiles[popupType].some(
                (tempFile) => tempFile.name === file.name,
              ),
          );

    renderSelectedFiles(popupType, newFiles, fileListContainer);
    toggleSubmitButton(popupType);
  }

  /**
   * Updates the file counter
   */
  function updateFileCounter(popupType, operation = '') {
    const fileCounter = document.querySelector(
      `.js-popup[data-type="${popupType}"] .js-popup-files-counter`,
    );
    const selectedFilesWrapper = document.querySelector(
      `.js-popup[data-type="${popupType}"] .js-selected-files-wrapper`,
    );
    const fileListContainer = document.querySelector(
      `.js-popup[data-type="${popupType}"] .js-selected-files-overview`,
    );

    if (fileCounter && operation !== 'zero') {
      fileCounter.textContent = tempUploadedFiles[popupType].length.toString();
    }

    let operationType = tempUploadedFiles[popupType]?.length ? 'add' : 'remove';
    fileListContainer && fileListContainer.classList.add('selected');
    selectedFilesWrapper &&
      selectedFilesWrapper.classList[operationType]('selected');

    if (fileCounter && operation === 'zero') {
      cleaningPopupTemporaries(popupType);
    }
  }

  /**
   * Activating the send button
   */
  function toggleSubmitButton(popupType) {
    const submitBtn = document.querySelector(
      `.js-popup[data-type="${popupType}"] .js-submit-btn`,
    );

    if (tempUploadedFiles[popupType].length > 0) {
      submitBtn.classList.remove('disabled');
    } else {
      submitBtn.classList.add('disabled');
    }
  }

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes

  /* File selection handler */
  [...fileInputs].forEach((fileInput) => {
    fileInput?.addEventListener('change', (event) => {
      if (!event.target?.files) return;

      const popupType = event.target.closest('.js-popup').dataset.type;
      const files = Array.from(event.target.files);
      let validFiles = [];

      files.forEach((file) => {
        if (file.size > MAX_FILE_SIZE) {
          alert(
            `File "${file.name}" exceeds the 5MB limit and will not be added.`,
          );
        } else {
          validFiles.push(file);
        }
      });

      (async () => {
        for (const file of validFiles) {
          const uploadResult = await uploadFileToServer(file);

          if (uploadResult && uploadResult.filename) {
            tempUploadedFiles[popupType].push({
              name: uploadResult.filename,
              url: uploadResult.url,
            });
          }
        }
        updateFileList(popupType, 'add');

        fileInput.value = '';
      })();
    });
  });

  document.body.addEventListener('click', (event) => {
    if (!event.target.classList.contains('js-selected-file-remove-btn')) return;

    const popupType = event.target.dataset.type;
    const fileItem = event.target.closest('.js-selected-file-item');
    const fileName =
      fileItem?.dataset.filename ||
      fileItem?.querySelector('.js-selected-file-name')?.textContent;

    if (!popupType || !fileName) return;

    const applyFormSelectedFiles = document.querySelector(
      `.js-uploading-type-wrapper[data-type="${popupType}"] .js-apply-form-selected-files`,
    );

    tempUploadedFiles[popupType] = tempUploadedFiles[popupType].filter(
      (file) => file.name !== fileName,
    );
    uploadedFiles[popupType] = uploadedFiles[popupType].filter(
      (file) => file.name !== fileName,
    );

    updateFileList(popupType);

    renderSelectedFiles(
      popupType,
      uploadedFiles[popupType],
      applyFormSelectedFiles,
    );

    // Updating a hidden form field
    if ('cv' === popupType) {
      // formFileInputCV.value = uploadedFiles[popupType].map(file => file.name).join(", ");
      formFileInputCV.value = uploadedFiles[popupType]
        .map((file) => file.url)
        .join(', ');
    }
    if ('cl' === popupType) {
      // formFileInputCL.value = uploadedFiles[popupType].map(file => file.name).join(", ");
      formFileInputCL.value = uploadedFiles[popupType]
        .map((file) => file.url)
        .join(', ');
    }

    updateFileCounter(popupType);
    toggleSubmitButton(popupType);
  });

  /*  Transferring files to the main form */
  submitBtns.forEach((submitBtn) => {
    submitBtn?.addEventListener('click', () => {
      const popupType = submitBtn.dataset.type;
      const uploadPopup = event.target?.closest('.js-popup');
      const applyFormSelectedFiles = document.querySelector(
        `.js-uploading-type-wrapper[data-type="${popupType}"] .js-apply-form-selected-files`,
      );

      if (tempUploadedFiles[popupType].length === 0) return;

      // Move temporary files to the main array
      uploadedFiles[popupType].push(...tempUploadedFiles[popupType]);

      renderSelectedFiles(
        popupType,
        uploadedFiles[popupType],
        applyFormSelectedFiles,
      );

      console.log('uploadedFiles', uploadedFiles);

      // Updating a hidden form field
      if ('cv' === popupType) {
        formFileInputCV.value = uploadedFiles[popupType]
          .map((file) => file.url)
          .join(', ');
      }
      if ('cl' === popupType) {
        formFileInputCL.value = uploadedFiles[popupType]
          .map((file) => file.url)
          .join(', ');
      }

      uploadPopup.classList.remove('active');

      cleaningPopupTemporaries(popupType);
      toggleSubmitButton(popupType);
    });
  });
};

/**
 * Handles switching between different CV variants by toggling the "activated" class.
 * Ensures that only one variant can be active at a time.
 */
export const switchingCVVariants = () => {
  const openCVTypes = document.querySelectorAll('.js-open-cv-type');

  openCVTypes.forEach((openCVType) => {
    openCVType.addEventListener('click', (event) => {
      event.stopPropagation();
      event.preventDefault();

      const parentSection = event.currentTarget?.closest('.js-cv-section');
      if (!parentSection) return;

      // Remove "activated" class from all other elements
      parentSection.querySelectorAll('.js-open-cv-type').forEach((el) => {
        if (el !== event.currentTarget) {
          el.classList.remove('activated');
          // Hide their related content
          el.nextElementSibling?.classList.remove('active');
        }
      });

      // Toggle "activated" class on the clicked element
      event.currentTarget?.classList.toggle('activated');
      event.currentTarget?.nextElementSibling?.classList.toggle('active'); // Show/hide related content
    });
  });
};

/**
 * Tracks the number of characters entered the textarea.
 * Displays the count in the counter element and prevents entering more than 1500 characters.
 */
export const enteredLetterCounting = () => {
  // Get all containers that include a textarea and a character counter
  const fieldWithCounterWrappers = document.querySelectorAll(
    '.js-field-with-counter-wrapper',
  );

  // If no such containers exist, exit the function
  if (!fieldWithCounterWrappers.length) return;

  fieldWithCounterWrappers.forEach((wrapper) => {
    // Find the counter element and the textarea inside the wrapper
    const counterValue = wrapper.querySelector('.js-letter-counter-value');
    const inputEl = wrapper.querySelector('textarea');

    // If either element is missing, skip this wrapper
    if (!inputEl || !counterValue) return;

    // Set the maximum allowed number of characters
    const maxLength = wrapper.dataset.maxcount;

    /**
     * Event listener for the textarea input event.
     * Updates the character counter and prevents exceeding the max limit.
     *
     * @param {Event} event - The input event triggered on typing.
     */
    inputEl.addEventListener('input', (event) => {
      const text = event.target?.value;

      // If the character limit is exceeded, trim the excess characters
      if (text.length > maxLength) {
        event.target.value = text.slice(0, maxLength);
      }

      // Update the character counter with the current text length
      counterValue.textContent = event.target?.value.length;
    });
  });
};

/**
 * Initializes a Swiper instance for the latest news section.
 *
 * - Selects the `.js-latest-news-swiper` element.
 * - Configures Swiper with navigation and responsive breakpoints.
 * - Displays 3 slides per view on screens ≥768px and 1 slide per view on smaller screens.
 *
 * @function swiperInitHandler
 * @returns {void}
 */
export const swiperInitHandler = () => {
  const latestNewSwiper = document.querySelector('.js-latest-news-swiper');
  const carouselWithLogosSwiper = document.querySelector(
    '.js-carousel-with-logos',
  );

  if (latestNewSwiper) {
    new Swiper('.js-latest-news-swiper', {
      loop: false,
      slidesPerView: 3,
      spaceBetween: 20,
      navigation: {
        nextEl: '.news-section__latest-news-next',
        prevEl: '.news-section__latest-news-prev',
      },
      breakpoints: {
        [mediaBreakpoints.md]: {
          slidesPerView: 3,
          spaceBetween: 20,
        },
        0: {
          slidesPerView: 1,
          spaceBetween: 10,
        },
      },
    });
  }

  if (carouselWithLogosSwiper) {
    new Swiper('.js-carousel-with-logos', {
      loop: true,
      slidesPerView: 'auto',
      spaceBetween: 60,
      autoplay: {
        delay: 1000,
        disableOnInteraction: true,
      },
    });
  }
};

/**
 * Initializes a scroll-to-top button.
 *
 * - Listens for clicks on the `.scroll-to-top-btn` element.
 * - Smoothly scrolls the window to the top when clicked.
 *
 * @function scrollToTop
 * @returns {void}
 */
export const scrollToTop = () => {
  const scrollToTopBtn = document.querySelector('.scroll-to-top-btn');

  if (!scrollToTopBtn) return;

  scrollToTopBtn.addEventListener('click', function () {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  });
};

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);

  if (parts.length === 2) return parts.pop()?.split(';').shift();

  return null;
}

/**
 * Handles the cookie consent banner functionality.
 *
 * This function checks if the user has already accepted cookies.
 * If not, it displays the cookie consent banner. The user can accept, decline,
 * or close the banner.
 *
 * - Adds `opened` class to the banner if cookies are not accepted.
 * - Listens for user interactions to accept or decline cookies.
 * - Stores user preference in localStorage.
 * - Removes the banner when an action is taken.
 */
export const acceptCookieFunc = () => {
  const ONE_DAY_SECONDS = 60 * 60 * 24;
  const HALF_YEAR_SECONDS = ONE_DAY_SECONDS * 30 * 6;
  const COOKIE_NAME = 'cookie_accepted';

  const ACCEPT_COOKIE_BOX = document.querySelector('.js-accept-cookie-box');

  const COOKIE_REJECT__KEY = 'cookie_reject_expiration';
  const cookieRejectExpiration = localStorage.getItem(COOKIE_REJECT__KEY);

  if (ACCEPT_COOKIE_BOX === null || getCookie(COOKIE_NAME)) return;

  if (cookieRejectExpiration == null || getCookie(COOKIE_NAME) == null) {
    // clear all cookies
    document.cookie.split(';').forEach((cookie) => {
      const name = cookie.split('=')[0].trim();
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });
  }

  if (cookieRejectExpiration !== null && getCookie(COOKIE_NAME) == null) {
    let cookieRejectExpirationParsed = null;

    try {
      cookieRejectExpirationParsed = JSON.parse(cookieRejectExpiration);
    } catch (e) {
      console.error('Failed to parse cookie rejection expiration:', e);
    }

    if (
      cookieRejectExpirationParsed &&
      new Date(cookieRejectExpirationParsed).getTime() >= Date.now()
    )
      return;
  }

  ACCEPT_COOKIE_BOX.classList.add('opened');
  document.body.classList.add('accept-cookie-box-is-opened');

  document.body.addEventListener('click', async (event) => {
    const cookieAction = event.target.dataset.cookie;

    if (cookieAction) {
      const acceptedState = cookieAction === 'accept';

      if (acceptedState) {
        document.cookie = `${COOKIE_NAME}=${acceptedState}; path=/; max-age=${HALF_YEAR_SECONDS}`;
      } else {
        const now = new Date();
        now.setDate(now.getDate() + 1);

        localStorage.setItem(COOKIE_REJECT__KEY, JSON.stringify(now));
      }

      ACCEPT_COOKIE_BOX.classList.remove('opened');
      document.body.classList.remove('accept-cookie-box-is-opened');
    }
  });
};

/**
 * Initializes and starts Lottie animations for all elements with the class "js-lottie-animation".
 * Ensures that animations are loaded from the data attribute "data-json-url".
 *
 * @function startLottiesAnimation
 * @returns {void}
 */
export const startLottiesAnimation = () => {
  const lottieContainers = document.querySelectorAll('.js-lottie-animation');

  if (lottieContainers.length === 0) return;

  const animations = [];

  lottieContainers.forEach((lottieContainer) => {
    if (typeof lottie === 'undefined') return;

    const jsonUrl = lottieContainer.dataset.jsonUrl;
    const playOnce = lottieContainer.dataset.playOnce === 'true';
    const playOnHover = lottieContainer.dataset.playOnHover === 'true';
    const segmentRaw = lottieContainer.dataset.segment;
    const segment = segmentRaw ? segmentRaw.split('-').map(Number) : null;
    const loopSegment = lottieContainer.dataset.loopSegment === 'true';

    let anim = null;
    let loading = false;

    const observer = new IntersectionObserver(
      async ([entry]) => {
        if (entry.isIntersecting) {
          if (!anim && !loading) {
            loading = true;

            let animationData;
            try {
              const response = await fetch(jsonUrl);
              animationData = await response.json();
            } catch {
              return;
            }

            anim = lottie.loadAnimation({
              container: lottieContainer,
              renderer: 'svg',
              loop: !playOnce && !segment,
              autoplay: false,
              animationData,
            });

            animations.push(anim);

            let hovering = false;
            let dir = 1;

            const hoverTarget = lottieContainer.closest('a') ?? lottieContainer;

            if (segment && loopSegment) {
              anim.addEventListener('complete', () => {
                anim.playSegments(segment, true);
              });
            } else if (segment) {
              anim.addEventListener('complete', () => {
                if (hovering) {
                  dir = -dir;
                  anim.setDirection(dir);
                  anim.playSegments(dir === 1 ? segment : [...segment].reverse(), true);
                } else {
                  anim.setDirection(1);
                  anim.goToAndPlay(0, true);
                }
              });

              hoverTarget.addEventListener('mouseenter', () => {
                hovering = true;
                dir = 1;
                anim.setDirection(1);
                anim.playSegments(segment, true);
              });
              hoverTarget.addEventListener('mouseleave', () => {
                hovering = false;
                dir = 1;
                anim.setDirection(1);
                anim.goToAndPlay(0, true);
              });
            } else {
              hoverTarget.addEventListener('mouseenter', () => { hovering = true; anim.pause(); });
              hoverTarget.addEventListener('mouseleave', () => { hovering = false; anim.play(); });
            }

            if (segment && loopSegment) {
              // Background loop: start playing immediately so the opacity
              // fade reveals the moving animation instead of a blank frame.
              anim.playSegments(segment, true);
            } else {
              lottieContainer.addEventListener('transitionend', () => {
                anim.play();
                if (playOnce) observer.disconnect();
              }, { once: true });
            }

            lottieContainer.classList.add('lottie-animation--visible');
          } else if (anim) {
            anim.play();
          }
        } else if (anim && !playOnce) {
          anim.pause();
        }
      },
      { threshold: 0 },
    );

    observer.observe(lottieContainer);
  });

  document.addEventListener('visibilitychange', () => {
    animations.forEach((anim) => {
      if (document.hidden) {
        anim.pause();
      } else {
        anim.play();
      }
    });
  });
};

/**
 * Implements accordion behavior for all elements with the class "js-accordion-item".
 * Provides expand/collapse logic on click.
 *
 * @function handleAccordion
 * @returns {void}
 */
export const handleAccordion = () => {
  const accordionItems = document.querySelectorAll('.js-accordion-item');

  if (accordionItems.length === 0) return;

  accordionItems.forEach((accordionItem) => {
    const title = accordionItem.querySelector('.js-accordion-panel-title');
    const description = accordionItem.querySelector(
      '.js-accordion__panel-description',
    );

    title.addEventListener('click', () => {
      // Close all items
      accordionItems.forEach((el) => {
        if (el !== accordionItem) {
          el.classList.remove('active');
          description.style.maxHeight = '0px';
        }
      });

      // Open current item
      const isActive = accordionItem.classList.contains('active');
      accordionItem.classList.toggle('active');

      if (isActive) {
        description.style.maxHeight = '0px';
      } else {
        description.style.maxHeight = description.scrollHeight + 24 + 'px';
      }
    });
  });
};

/**
 * Observes an element and adds the `is-visible` class when it enters the viewport.
 * Supports optional staggered animation of child elements.
 *
 * @param {string|HTMLElement} selector - CSS selector string or DOM element to observe.
 * @param {object} [options]
 * @param {number} [options.threshold=0.3] - Intersection ratio threshold to trigger the animation.
 * @param {string|null} [options.childSelector=null] - If set, `is-visible` is added to matching children instead of the target itself.
 * @param {number} [options.stagger=0] - Delay in ms between each child animation when using childSelector.
 */
export const animateOnScroll = (selector, { threshold = 0.3, childSelector = null, stagger = 0 } = {}) => {
  const target = typeof selector === 'string' ? document.querySelector(selector) : selector;
  if (!target) return;

  const observer = new IntersectionObserver(([entry]) => {
    if (!entry.isIntersecting) return;
    if (childSelector) {
      target.querySelectorAll(childSelector).forEach((el, i) => {
        setTimeout(() => el.classList.add('is-visible'), i * stagger);
      });
    } else {
      target.classList.add('is-visible');
    }
    observer.unobserve(target);
  }, { threshold });

  observer.observe(target);
};

export const animateTextByLines = (selector, { delay = 0, lineStagger = 150, waitFor = null, lineAnimation = null } = {}) => {
  const el = typeof selector === 'string' ? document.querySelector(selector) : selector;
  if (!el) return Promise.resolve();

  el.style.opacity = '0';

  const animDuration = 600;

  function splitAndObserve() {
    return new Promise(resolve => {
      function wrapWords(node) {
        if (node.nodeType === Node.TEXT_NODE) {
          const frag = document.createDocumentFragment();
          node.textContent.split(/(\s+)/).forEach(part => {
            if (!part) return;
            if (/^\s+$/.test(part)) {
              frag.appendChild(document.createTextNode(part));
            } else {
              const span = document.createElement('span');
              span.className = 'js-split-word';
              span.style.display = 'inline-block';
              span.textContent = part;
              frag.appendChild(span);
            }
          });
          node.parentNode.replaceChild(frag, node);
        } else if (node.nodeType === Node.ELEMENT_NODE && !node.classList.contains('js-split-word')) {
          Array.from(node.childNodes).forEach(wrapWords);
        }
      }

      Array.from(el.childNodes).forEach(wrapWords);

      requestAnimationFrame(() => requestAnimationFrame(() => {
        const words = Array.from(el.querySelectorAll('.js-split-word'));
        if (!words.length) { resolve(); return; }

        const groups = [];
        let currentTop = null;
        let currentGroup = [];
        words.forEach(word => {
          const top = Math.round(word.getBoundingClientRect().top);
          if (currentTop === null) currentTop = top;
          if (Math.abs(top - currentTop) > 4) {
            groups.push(currentGroup);
            currentGroup = [];
            currentTop = top;
          }
          currentGroup.push(word.textContent);
        });
        if (currentGroup.length) groups.push(currentGroup);

        el.innerHTML = '';
        groups.forEach(lineWords => {
          const lineEl = document.createElement('span');
          lineEl.className = 'js-text-line';
          lineEl.style.cssText = lineAnimation
            ? 'display:block;opacity:0'
            : 'display:block;opacity:0;transform:translateY(60px)';
          lineEl.textContent = lineWords.join(' ');
          el.appendChild(lineEl);
        });
        el.style.opacity = '';

        const startObserving = () => {
          const observer = new IntersectionObserver(([entry]) => {
            if (!entry.isIntersecting) return;
            const lines = el.querySelectorAll('.js-text-line');
            lines.forEach((line, i) => {
              setTimeout(() => {
                line.style.animation = lineAnimation || 'performance-fade-in 0.6s ease-out forwards';
              }, delay + i * lineStagger);
            });
            setTimeout(resolve, delay + (lines.length - 1) * lineStagger + animDuration);
            observer.unobserve(el);
          }, { threshold: 0.3 });
          observer.observe(el);
        };

        if (waitFor) {
          waitFor.then(startObserving);
        } else {
          startObserving();
        }
      }));
    });
  }

  if (document.fonts && document.fonts.ready) {
    return document.fonts.ready.then(splitAndObserve);
  }
  return splitAndObserve();
};
